import { CloseCircleTwoTone, MailOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import { createStyles } from "antd-style";

import { Badge } from "@av/components/molecules/Badge";
import { resolveEmailStatusLabel } from "../../../../../services/admin-settings/admin-settings";
import { type EmailStatus } from "../../../../../services/admin-settings/type";

import type { TFunction } from "i18next";

type EmailStatusIndicatorProps = {
  emailStatus?: EmailStatus;
  t: TFunction;
  testIdPrefix: string;
  testIdValue: string;
};

const useStyles = createStyles(({ css, token }) => ({
  indicator: css`
    color: ${token.colorText};
    display: inline-flex;
    line-height: 0;
  `,
  failedMark: css`
    font-size: 8px;
  `,
}));

export function EmailStatusIndicator({
  emailStatus,
  t,
  testIdPrefix,
  testIdValue,
}: EmailStatusIndicatorProps) {
  const { styles, theme } = useStyles();
  const label = resolveEmailStatusLabel(t, emailStatus);

  if (!emailStatus || !label) {
    return null;
  }

  const mailIcon = <MailOutlined aria-hidden />;
  const indicatorTestId = `${testIdPrefix}-email-status-${emailStatus.toLowerCase()}-${testIdValue}`;

  return (
    <Popover
      title={<span data-testid={`${indicatorTestId}-tooltip`}>{label}</span>}
      trigger={["hover", "focus"]}
    >
      <span
        aria-label={label}
        className={styles.indicator}
        data-testid={indicatorTestId}
        tabIndex={0}
      >
        {emailStatus === "FAILED" ? (
          <Badge
            count={
              <CloseCircleTwoTone
                aria-hidden
                className={styles.failedMark}
                twoToneColor={theme.colorError}
              />
            }
            // Places the small mark on the envelope's bottom-right corner, as in Figma node 1501-9563
            offset={[-2, 12]}
            testIdPrefix={`${testIdPrefix}-email-status-failed-${testIdValue}`}
          >
            {mailIcon}
          </Badge>
        ) : (
          mailIcon
        )}
      </span>
    </Popover>
  );
}
